import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import { Close, RateReview, Person, Description } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { enhancedApi as adminApi } from '@store/generated/admin';
import { formatDateTime } from '../utils/formatting';
import { NoteFeedbackDisplay } from './note-feedback-display';
import { SectionFeedbackDisplay } from './section-feedback-display';
import type { NoteFeedbackEvent2, NoteSectionFeedbackEvent2 } from '@store/generated/admin';

const { useGetV2AdminFeedbackSessionsBySessionIdQuery } = adminApi;

interface FeedbackSessionDetailDialogProps {
  open: boolean;
  onClose: () => void;
  sessionId: string | null;
}

// Type guards
function isNoteFeedbackEvent(
  event: NoteFeedbackEvent2 | NoteSectionFeedbackEvent2,
): event is NoteFeedbackEvent2 {
  return event.feedback_type === 'note';
}

function isSectionFeedbackEvent(
  event: NoteFeedbackEvent2 | NoteSectionFeedbackEvent2,
): event is NoteSectionFeedbackEvent2 {
  return event.feedback_type === 'section';
}

// Calculate sentiment from events (fallback if backend doesn't provide it)
function calculateSentimentFromEvents(events: (NoteFeedbackEvent2 | NoteSectionFeedbackEvent2)[]): {
  sentiment: 'positive' | 'negative' | 'mixed' | 'neutral';
  positiveCount: number;
  negativeCount: number;
} {
  let positiveCount = 0;
  let negativeCount = 0;

  events.forEach(event => {
    if (isNoteFeedbackEvent(event)) {
      // Note ratings: 0=good, 1=bad, 2=neutral
      if (event.note_rating === 0) positiveCount++;
      else if (event.note_rating === 1) negativeCount++;
    } else if (isSectionFeedbackEvent(event)) {
      // Section ratings: 1=good, 0=bad
      if (event.section_rating === 1) positiveCount++;
      else if (event.section_rating === 0) negativeCount++;
    }
  });

  let sentiment: 'positive' | 'negative' | 'mixed' | 'neutral';
  if (negativeCount === 0 && positiveCount > 0) sentiment = 'positive';
  else if (positiveCount === 0 && negativeCount > 0) sentiment = 'negative';
  else if (positiveCount > 0 && negativeCount > 0) sentiment = 'mixed';
  else sentiment = 'neutral';

  return { sentiment, positiveCount, negativeCount };
}

export function FeedbackSessionDetailDialog({
  open,
  onClose,
  sessionId,
}: FeedbackSessionDetailDialogProps) {
  const navigate = useNavigate();

  const {
    data: session,
    isLoading,
    error,
  } = useGetV2AdminFeedbackSessionsBySessionIdQuery(
    { sessionId: sessionId || '' },
    { skip: !sessionId || !open },
  );

  // Separate note and section feedback events, sorted by timestamp
  const noteFeedbackEvents =
    session?.feedback_events
      .filter(isNoteFeedbackEvent)
      .sort((a, b) => a.timestamp - b.timestamp) || [];
  const sectionFeedbackEvents =
    session?.feedback_events
      .filter(isSectionFeedbackEvent)
      .sort((a, b) => a.timestamp - b.timestamp) || [];

  // Calculate sentiment from events (detail view doesn't have pre-calculated sentiment)
  const sentiment = session ? calculateSentimentFromEvents(session.feedback_events) : null;

  const handleNoteClick = () => {
    if (session?.note_id) {
      navigate(`/admin-v2/note/${session.note_id}`);
      onClose();
    }
  };

  const getSentimentDisplay = () => {
    if (!sentiment) return null;

    const config: Record<
      'positive' | 'negative' | 'mixed' | 'neutral' | 'comment_only',
      { emoji: string; label: string; color: 'success' | 'error' | 'warning' | 'default' | 'info' }
    > = {
      positive: { emoji: '🟢', label: 'Positive', color: 'success' },
      negative: { emoji: '🔴', label: 'Negative', color: 'error' },
      mixed: { emoji: '🟡', label: 'Mixed', color: 'warning' },
      neutral: { emoji: '⚪', label: 'Neutral', color: 'default' },
      comment_only: { emoji: '💬', label: 'Comments Only', color: 'info' },
    };

    const sentimentConfig = config[sentiment.sentiment] || config.neutral;
    return (
      <Chip
        icon={<Box>{sentimentConfig.emoji}</Box>}
        label={`${sentimentConfig.label} (${sentiment.positiveCount} 👍 / ${sentiment.negativeCount} 👎)`}
        color={sentimentConfig.color}
        variant="outlined"
        size="small"
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <RateReview color="primary" />
            <Typography variant="h6">Feedback Session Details</Typography>
          </Box>
          <Button onClick={onClose} size="small" startIcon={<Close />}>
            Close
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box py={4}>
            <Typography color="error" align="center">
              Failed to load session details
            </Typography>
          </Box>
        )}

        {session && (
          <Box>
            {/* Summary Stats */}
            <Box mb={3}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Session Summary
                </Typography>
                {getSentimentDisplay()}
              </Box>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Chip
                  label={`${session.feedback_events.length} Total Event${session.feedback_events.length !== 1 ? 's' : ''}`}
                  size="small"
                  variant="outlined"
                />
                {noteFeedbackEvents.length > 0 && (
                  <Chip
                    label={`${noteFeedbackEvents.length} Note Feedback`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                )}
                {sectionFeedbackEvents.length > 0 && (
                  <Chip
                    label={`${sectionFeedbackEvents.length} Section Feedback`}
                    size="small"
                    variant="outlined"
                    color="secondary"
                  />
                )}
              </Stack>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Session Metadata */}
            <Box mb={3}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom mb={1}>
                Session Information
              </Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <Person fontSize="small" color="action" sx={{ mt: 0.5 }} />
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      Provider
                    </Typography>
                    {session.user_name && (
                      <Typography variant="body2">{session.user_name}</Typography>
                    )}
                    {session.user_email && (
                      <Typography variant="caption" color="text.secondary">
                        {session.user_email}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontFamily="monospace"
                      display="block"
                    >
                      ID: {session.user_id}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="flex-start" gap={1}>
                  <Description fontSize="small" color="action" sx={{ mt: 0.5 }} />
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      Note
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
                        {session.note_id}
                      </Typography>
                      <Button size="small" variant="outlined" onClick={handleNoteClick}>
                        View Note
                      </Button>
                    </Box>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 100 }}>
                    Last Modified:
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(new Date(session.last_modified * 1000).toISOString())}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Feedback Events */}
            {session.feedback_events.length === 0 ? (
              <Box py={4}>
                <Typography variant="body2" color="text.secondary" align="center">
                  No feedback events in this session
                </Typography>
              </Box>
            ) : (
              <Box>
                {/* Note Feedback Section */}
                {noteFeedbackEvents.length > 0 && (
                  <Box mb={4}>
                    <Typography variant="h6" fontWeight="medium" gutterBottom mb={2}>
                      📝 Overall Note Feedback ({noteFeedbackEvents.length})
                    </Typography>
                    {noteFeedbackEvents.map(event => (
                      <NoteFeedbackDisplay key={event.feedback_event_id} event={event} />
                    ))}
                  </Box>
                )}

                {/* Section Feedback Section */}
                {sectionFeedbackEvents.length > 0 && (
                  <Box mb={3}>
                    <Typography variant="h6" fontWeight="medium" gutterBottom mb={2}>
                      📋 Section Feedback ({sectionFeedbackEvents.length})
                    </Typography>
                    {sectionFeedbackEvents.map(event => (
                      <SectionFeedbackDisplay key={event.feedback_event_id} event={event} />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
